import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';

/**
 * Reusable searchable multi-select dropdown for Specialties.
 *
 *  - Selected items render as chips above the input
 *  - Click/focus the input → opens a dropdown list below (anchored)
 *  - Type to filter; click outside or press Escape to close
 *  - `disabledIds` keeps a subset of options non-deselectable (e.g., locked specialties)
 */
export function SpecialtyMultiSelect({
  specialties = [],
  value = [],
  onChange,
  placeholder = 'Search specialties...',
  testidPrefix = 'spec',
  disabledIds = [],
  emptyText = 'No specialties available',
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Note: Intentionally NOT listening for Escape — letting it bubble normally so it
  // closes any parent Radix Dialog cleanly. Users close the dropdown by clicking
  // outside, clicking the chevron toggle, or selecting an option.

  const toggle = (id) => {
    if (disabledIds.includes(id)) return;
    const has = value.includes(id);
    onChange(has ? value.filter((x) => x !== id) : [...value, id]);
  };

  const remove = (id) => {
    if (disabledIds.includes(id)) return;
    onChange(value.filter((x) => x !== id));
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? specialties.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q))
    : specialties;

  return (
    <div className="relative" ref={containerRef} data-testid={`${testidPrefix}-multiselect`}>
      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testidPrefix}-selected-chips`}>
          {value.map((id) => {
            const spec = specialties.find((s) => s.id === id);
            if (!spec) return null;
            const locked = disabledIds.includes(id);
            return (
              <Badge
                key={id}
                variant="secondary"
                className="flex items-center gap-1 bg-blue-100 text-blue-800"
              >
                {spec.name} ({spec.code})
                {!locked && (
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-red-500"
                    onClick={() => remove(id)}
                    data-testid={`${testidPrefix}-remove-${spec.code}`}
                  />
                )}
              </Badge>
            );
          })}
        </div>
      )}

      {/* Input trigger */}
      <div
        className={`relative border rounded-md bg-white transition-all ${
          open ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-300'
        }`}
      >
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-9 pr-9 h-9 text-sm bg-transparent focus:outline-none rounded-md"
          data-testid={`${testidPrefix}-search`}
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
          aria-label={open ? 'Close' : 'Open'}
          data-testid={`${testidPrefix}-toggle`}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testidPrefix}-dropdown`}
        >
          {specialties.length === 0 ? (
            <p className="text-sm text-slate-400 italic px-3 py-3">{emptyText}</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 italic px-3 py-3">
              No specialties found matching &quot;{search}&quot;
            </p>
          ) : (
            <div className="py-1">
              {filtered.map((spec) => {
                const checked = value.includes(spec.id);
                const locked = disabledIds.includes(spec.id);
                // One single label wraps checkbox + text. Native browser label-to-checkbox
                // association toggles the checkbox once on any click within the row. No
                // double-handlers, no propagation tricks → no setState/ref cascade.
                const inputId = `${testidPrefix}-cb-${spec.id}`;
                return (
                  <label
                    key={spec.id}
                    htmlFor={inputId}
                    className={`flex items-center gap-2 px-3 py-2 hover:bg-slate-50 ${
                      locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    data-testid={`${testidPrefix}-option-${spec.code}`}
                  >
                    <Checkbox
                      id={inputId}
                      checked={checked}
                      onCheckedChange={() => toggle(spec.id)}
                      disabled={locked}
                    />
                    <span className="font-normal text-sm flex-1 leading-none">
                      {spec.name} <span className="text-slate-400">({spec.code})</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SpecialtyMultiSelect;
